export type EducationCategory = 'ortodoncia' | 'endodoncia' | 'prevencion' | 'urgencias';

export type DentalEducationArticle = {
  slug: string;
  category: EducationCategory;
  title: string;
  eyebrow: string;
  description: string;
  readingTime: string;
  heroSummary: string;
  keyTakeaways: string[];
  sections: { heading: string; body: string[] }[];
  glossary: { term: string; definition: string }[];
  faqs: { question: string; answer: string }[];
  relatedSlugs: string[];
  sources: { label: string; url: string }[];
};

export const educationCategories: Record<EducationCategory, { label: string; shortLabel: string; description: string }> = {
  ortodoncia: {
    label: 'Ortodoncia',
    shortLabel: 'Brackets',
    description: 'Aprende cómo se corrige la mordida, qué tipos de brackets existen y cómo cuidar tu tratamiento.',
  },
  endodoncia: {
    label: 'Endodoncia',
    shortLabel: 'Endodoncia',
    description: 'Información clara sobre dolor dental, nervio inflamado, infección, tratamiento de conductos y recuperación.',
  },
  prevencion: {
    label: 'Prevención',
    shortLabel: 'Prevención',
    description: 'Hábitos, limpieza, flúor, placa bacteriana y señales tempranas para evitar problemas mayores.',
  },
  urgencias: {
    label: 'Urgencias dentales',
    shortLabel: 'Urgencias',
    description: 'Qué hacer ante dolor fuerte, inflamación, fracturas dentales o sospecha de infección.',
  },
};

export const dentalEducationArticles: DentalEducationArticle[] = [
  {
    slug: 'que-es-una-endodoncia',
    category: 'endodoncia',
    eyebrow: 'Guía de endodoncia',
    title: '¿Qué es una endodoncia y cuándo se necesita?',
    description: 'Explicación completa del tratamiento de conductos, síntomas, proceso, recuperación y señales de alerta.',
    readingTime: '8 min',
    heroSummary: 'La endodoncia busca conservar un diente cuando la pulpa dental —el tejido interno con nervios y vasos sanguíneos— está inflamada, infectada o dañada. En lugar de extraer la pieza, se limpian los conductos, se desinfectan y se sellan para que el diente pueda seguir funcionando.',
    keyTakeaways: [
      'No todo dolor dental requiere endodoncia; primero se necesita valoración clínica y radiográfica.',
      'Puede ser necesaria cuando hay caries profunda, trauma, infección, absceso o sensibilidad persistente.',
      'Después del tratamiento suele requerirse restauración definitiva, y en muchos casos corona, para proteger el diente.',
      'Dolor con inflamación facial, fiebre, pus o dificultad para abrir la boca amerita atención urgente.',
    ],
    sections: [
      {
        heading: 'Qué significa “tratamiento de conductos”',
        body: [
          'Dentro del diente existe un espacio llamado cámara pulpar y conductos radiculares. Ahí se encuentra la pulpa dental. Cuando una caries avanza demasiado, una restauración queda muy profunda, hay una fractura o un golpe, esa pulpa puede inflamarse de forma irreversible o infectarse.',
          'La endodoncia consiste en retirar ese tejido dañado, limpiar y conformar los conductos, desinfectar el interior y rellenarlo con un material biocompatible. El objetivo es eliminar la fuente del dolor o infección y conservar la pieza dental siempre que sea posible.',
        ],
      },
      {
        heading: 'Síntomas que pueden indicar problema de nervio',
        body: [
          'Los signos más comunes son dolor espontáneo, dolor que despierta por la noche, sensibilidad al frío o calor que tarda en desaparecer, dolor al masticar, cambio de color en el diente, inflamación de encía, fístula —un granito por donde drena pus— o mal sabor persistente.',
          'También puede existir infección sin dolor intenso. Por eso una radiografía y pruebas clínicas de sensibilidad, percusión y mordida son importantes antes de decidir el tratamiento.',
        ],
      },
      {
        heading: 'Cómo suele ser el procedimiento',
        body: [
          'Primero se realiza diagnóstico y anestesia local. Después se aísla el diente, se accede a la cámara pulpar, se localizan los conductos y se limpian con instrumentos y soluciones irrigantes. Finalmente se sellan los conductos y se coloca una restauración provisional o definitiva según el caso.',
          'La cantidad de citas depende del diagnóstico, anatomía del diente, presencia de infección, dolor activo, restauraciones previas y dificultad técnica. Molares suelen requerir más tiempo porque tienen más conductos que dientes anteriores.',
        ],
      },
      {
        heading: 'Qué esperar después',
        body: [
          'Es normal presentar sensibilidad al morder o molestia leve durante algunos días, especialmente si el diente llegó inflamado o infectado. Esa molestia debe ir disminuyendo. No es normal que aumente progresivamente, aparezca inflamación marcada o fiebre.',
          'El diente tratado ya no tiene pulpa vital, pero sigue rodeado por ligamento, hueso y encía, por eso puede sentirse sensible mientras cicatrizan los tejidos alrededor de la raíz.',
        ],
      },
      {
        heading: 'Por qué la restauración final es clave',
        body: [
          'Una endodoncia bien realizada puede fracasar si el diente queda filtrado, fracturado o con una restauración débil. En dientes posteriores, cuando hay pérdida importante de estructura, la corona o incrustación puede ser necesaria para distribuir fuerzas y reducir riesgo de fractura.',
          'La recomendación correcta depende de cuánta estructura sana queda, tipo de mordida, hábitos como bruxismo y tamaño de la restauración previa.',
        ],
      },
    ],
    glossary: [
      { term: 'Pulpa dental', definition: 'Tejido interno del diente que contiene nervios, vasos sanguíneos y tejido conectivo.' },
      { term: 'Conducto radicular', definition: 'Espacio dentro de la raíz por donde pasa la pulpa dental.' },
      { term: 'Absceso', definition: 'Acumulación de pus asociada a infección; puede causar inflamación, dolor y drenaje.' },
      { term: 'Fístula', definition: 'Pequeña salida en encía por donde puede drenar una infección crónica.' },
    ],
    faqs: [
      { question: '¿La endodoncia duele?', answer: 'Con anestesia local el procedimiento no debería doler. Puede haber presión o vibración. Si el diente está muy inflamado, el control anestésico puede requerir técnicas adicionales.' },
      { question: '¿Es mejor extraer el diente?', answer: 'No siempre. Si el diente puede restaurarse y tiene soporte adecuado, conservarlo suele ser preferible. La extracción puede requerir implante, puente o prótesis para recuperar función.' },
      { question: '¿Puedo comer después?', answer: 'Depende del tipo de restauración colocada y de la anestesia. En general conviene evitar masticar fuerte con ese lado hasta tener restauración definitiva.' },
    ],
    relatedSlugs: ['dolor-dental-cuando-es-urgencia', 'diccionario-dental-basico', 'caries-placa-y-prevencion'],
    sources: [
      { label: 'American Dental Association - Root canal treatment', url: 'https://jada.ada.org/article/S0002-8177(14)61651-2/fulltext' },
      { label: 'NOM-013-SSA2-2015, prevención y control de enfermedades bucales', url: 'https://www.cenaprece.salud.gob.mx/programas/interior/saludbucal/descargas/pdf/NORMA013.pdf' },
    ],
  },
  {
    slug: 'brackets-metalicos-esteticos-y-cuidados',
    category: 'ortodoncia',
    eyebrow: 'Guía de ortodoncia',
    title: 'Brackets metálicos, estéticos y cuidados durante ortodoncia',
    description: 'Qué hacen los brackets, diferencias entre opciones, cuidados diarios y señales para acudir a revisión.',
    readingTime: '9 min',
    heroSummary: 'Los brackets son aparatos fijos que usan pequeñas fuerzas controladas para mover los dientes y mejorar alineación, mordida, estética y función. La elección entre metálicos o estéticos depende del diagnóstico, presupuesto, hábitos, expectativas y recomendación clínica.',
    keyTakeaways: [
      'Los brackets no sólo alinean dientes; también ayudan a corregir relaciones de mordida.',
      'Los metálicos suelen ser resistentes y eficientes; los estéticos son menos visibles pero requieren cuidados específicos.',
      'La higiene es crítica: placa alrededor del bracket puede causar manchas blancas, caries o inflamación de encía.',
      'Las citas de control permiten ajustar fuerzas, revisar avances y prevenir complicaciones.',
    ],
    sections: [
      {
        heading: 'Cómo funcionan los brackets',
        body: [
          'Cada bracket se adhiere al diente y se conecta con un arco. El arco, ligas y otros accesorios aplican fuerzas ligeras y constantes para guiar el movimiento dental. El hueso alrededor del diente se remodela: se reabsorbe en un lado y se forma en otro.',
          'Este proceso biológico necesita tiempo. Aplicar fuerzas excesivas no significa mover más rápido; puede causar dolor, reabsorción radicular o pérdida de control del movimiento.',
        ],
      },
      {
        heading: 'Brackets metálicos vs estéticos',
        body: [
          'Los brackets metálicos son la opción clásica: resistentes, predecibles y generalmente más accesibles. Son visibles, pero permiten muy buen control en la mayoría de los casos.',
          'Los brackets estéticos pueden ser cerámicos o de materiales translúcidos/color diente. Se notan menos en sonrisa, pero pueden requerir mayor cuidado para evitar pigmentaciones en ligas, fracturas o molestias por volumen según el caso.',
        ],
      },
      {
        heading: 'Qué cuidados son obligatorios',
        body: [
          'Cepillar después de comer es fundamental. Deben limpiarse dientes, encía, alrededor del bracket y debajo del arco. Los cepillos interdentales, hilo dental especial para ortodoncia e irrigadores pueden ayudar, pero no sustituyen una técnica adecuada.',
          'Se recomienda evitar alimentos muy duros, pegajosos o que obliguen a morder con fuerza directa: caramelos, hielo, elotes enteros, chicles, tostadas muy duras o nueces sin cuidado. Un bracket despegado puede atrasar el tratamiento.',
        ],
      },
      {
        heading: 'Duración del tratamiento',
        body: [
          'No existe una duración universal. Casos sencillos pueden tomar menos tiempo, mientras que apiñamiento severo, mordidas complejas, extracciones, falta de cooperación o citas perdidas pueden alargar el proceso.',
          'La duración se estima después de diagnóstico con exploración, fotografías, radiografías, modelos o escaneo y análisis de mordida.',
        ],
      },
      {
        heading: 'Retenedores: la etapa que no se debe saltar',
        body: [
          'Al terminar ortodoncia, los dientes tienden a moverse por memoria de fibras, crecimiento, fuerzas musculares y hábitos. Por eso se indican retenedores fijos, removibles o una combinación.',
          'Usarlos como se indica es parte del tratamiento, no un extra opcional. Si se dejan de usar, puede haber recidiva y requerir corrección nuevamente.',
        ],
      },
    ],
    glossary: [
      { term: 'Bracket', definition: 'Pequeña pieza adherida al diente para sujetar el arco de ortodoncia.' },
      { term: 'Arco', definition: 'Alambre que conecta brackets y transmite fuerzas para mover dientes.' },
      { term: 'Ligadura', definition: 'Liga o elemento que sujeta el arco al bracket.' },
      { term: 'Retenedor', definition: 'Aparato usado después de ortodoncia para mantener la nueva posición dental.' },
    ],
    faqs: [
      { question: '¿Los brackets estéticos son igual de buenos?', answer: 'Pueden ser una excelente opción en muchos casos. La indicación depende del diagnóstico, tipo de movimiento requerido, mordida y hábitos del paciente.' },
      { question: '¿Cada cuánto son las citas?', answer: 'Varía según la fase del tratamiento. Muchos controles son mensuales o cada varias semanas, pero el odontólogo/ortodoncista define el intervalo adecuado.' },
      { question: '¿Qué hago si se despega un bracket?', answer: 'No lo arranques ni lo pegues por tu cuenta. Agenda revisión; si lastima, se puede cubrir temporalmente con cera de ortodoncia.' },
    ],
    relatedSlugs: ['cuanto-dura-la-ortodoncia', 'diccionario-dental-basico', 'caries-placa-y-prevencion'],
    sources: [
      { label: 'American Association of Orthodontists - Braces', url: 'https://aaoinfo.org/treatments/braces/' },
      { label: 'American Association of Orthodontists - Life during treatment', url: 'https://aaoinfo.org/whats-trending/life-during-treatment/' },
    ],
  },
  {
    slug: 'cuanto-dura-la-ortodoncia',
    category: 'ortodoncia',
    eyebrow: 'Planeación de ortodoncia',
    title: '¿Cuánto dura un tratamiento de ortodoncia?',
    description: 'Factores reales que cambian la duración: diagnóstico, mordida, edad, higiene, cooperación y citas.',
    readingTime: '7 min',
    heroSummary: 'La duración de la ortodoncia depende de la complejidad del caso y de la respuesta biológica de cada paciente. No se mide sólo por “qué tan chuecos” se ven los dientes, sino por la mordida completa, salud periodontal, objetivos y constancia.',
    keyTakeaways: [
      'La mordida puede ser más importante que la estética frontal para estimar duración.',
      'Citas perdidas, brackets despegados y mala higiene suelen retrasar avances.',
      'Los movimientos seguros requieren control; ir demasiado rápido no siempre es sano.',
      'El retenedor posterior es indispensable para conservar resultados.',
    ],
    sections: [
      { heading: 'Factores que más influyen', body: ['Influyen el apiñamiento, espacios, mordida abierta, mordida profunda, mordida cruzada, relación entre maxilar y mandíbula, extracciones, dientes retenidos, enfermedad periodontal, edad y cooperación.', 'También cuenta la técnica utilizada, calidad de higiene, asistencia a citas, uso de elásticos y cumplimiento de indicaciones. Dos pacientes con sonrisas similares pueden tener planes muy distintos.'] },
      { heading: 'Por qué no conviene prometer tiempos exactos', body: ['La ortodoncia depende de respuesta biológica. El especialista puede dar un estimado razonable, pero el plan se ajusta conforme se observa movimiento, estabilidad y salud de encías y raíces.', 'La prioridad debe ser mover dientes con fuerzas controladas, mantener tejidos sanos y lograr una mordida funcional, no sólo “quitar lo chueco” lo más rápido posible.'] },
      { heading: 'Cómo ayudar a que avance mejor', body: ['Asiste a tus controles, evita despegar brackets, usa elásticos si fueron indicados, mantén higiene estricta y reporta molestias o accesorios sueltos. La cooperación del paciente puede marcar una diferencia enorme.', 'Las limpiezas dentales durante ortodoncia también ayudan cuando hay acumulación de sarro, inflamación o zonas difíciles de limpiar.'] },
    ],
    glossary: [
      { term: 'Apiñamiento', definition: 'Falta de espacio que provoca dientes encimados o girados.' },
      { term: 'Mordida cruzada', definition: 'Relación invertida entre dientes superiores e inferiores en alguna zona.' },
      { term: 'Elásticos intermaxilares', definition: 'Ligas usadas para ayudar a corregir la relación entre arcadas.' },
    ],
    faqs: [
      { question: '¿Los alineadores son más rápidos que brackets?', answer: 'No necesariamente. Depende del caso, del plan y de la cooperación. Algunos movimientos son más predecibles con brackets y otros pueden resolverse con alineadores bien indicados.' },
      { question: '¿Duele cada ajuste?', answer: 'Puede haber presión o sensibilidad algunos días. Dolor severo, úlceras persistentes o piezas flojas fuera de lo esperado deben revisarse.' },
    ],
    relatedSlugs: ['brackets-metalicos-esteticos-y-cuidados', 'diccionario-dental-basico', 'caries-placa-y-prevencion'],
    sources: [
      { label: 'American Association of Orthodontists - Braces', url: 'https://aaoinfo.org/treatments/braces/' },
      { label: 'American Association of Orthodontists - Orthodontic glossary', url: 'https://aaoinfo.org/resources/glossary-of-orthodontic-terms/' },
    ],
  },
  {
    slug: 'caries-placa-y-prevencion',
    category: 'prevencion',
    eyebrow: 'Prevención dental',
    title: 'Caries, placa bacteriana y prevención: lo básico que sí importa',
    description: 'Cómo se forma la caries, por qué se inflama la encía y qué hábitos ayudan a prevenir tratamientos costosos.',
    readingTime: '8 min',
    heroSummary: 'La prevención dental no se trata sólo de “cepillarse más”. Se trata de controlar placa bacteriana, frecuencia de azúcares, técnica de higiene, flúor, limpiezas y revisión temprana para actuar antes de que aparezca dolor.',
    keyTakeaways: [
      'La caries es un proceso progresivo; al inicio puede no doler.',
      'La placa bacteriana produce ácidos cuando hay exposición frecuente a azúcares fermentables.',
      'La higiene interdental es clave porque el cepillo no entra bien entre dientes.',
      'La NOM mexicana de salud bucal promueve control de placa, limpieza interdental y fluoruros tópicos como medidas preventivas.',
    ],
    sections: [
      { heading: 'Qué es la placa bacteriana', body: ['La placa es una película pegajosa de bacterias y restos que se forma sobre dientes, encías y restauraciones. Si no se retira, puede favorecer caries, gingivitis, mal aliento y sarro.', 'El sarro es placa endurecida/mineralizada. No se elimina con cepillo; requiere limpieza profesional.'] },
      { heading: 'Por qué aparece caries', body: ['Las bacterias metabolizan azúcares y producen ácidos que desmineralizan el esmalte. Si el ataque ácido se repite muchas veces y no hay suficiente remineralización, aparece una lesión de caries.', 'No sólo importa la cantidad de azúcar, también la frecuencia. Estar “picando” bebidas azucaradas, pan dulce, dulces o snacks durante el día mantiene el pH bajo por más tiempo.'] },
      { heading: 'Hábitos preventivos con mejor impacto', body: ['Cepillado con técnica correcta, pasta fluorada, limpieza interdental diaria, reducción de frecuencia de azúcares, agua simple, controles periódicos y atención temprana de manchas, sensibilidad o sangrado.', 'En niños, adolescentes, pacientes con ortodoncia o alto riesgo de caries, el odontólogo puede indicar medidas adicionales como selladores, barnices de flúor o controles más frecuentes.'] },
      { heading: 'Sangrado de encía: no es normal', body: ['Si sangra al cepillar o usar hilo, normalmente hay inflamación. La respuesta correcta no es dejar de limpiar, sino mejorar técnica y acudir a revisión para retirar placa/sarro y descartar enfermedad periodontal.', 'Una encía sana suele verse firme, sin inflamación marcada y sin sangrado habitual.'] },
    ],
    glossary: [
      { term: 'Placa bacteriana', definition: 'Biofilm de bacterias que se adhiere a dientes y encías.' },
      { term: 'Sarro', definition: 'Placa mineralizada que requiere limpieza profesional.' },
      { term: 'Flúor tópico', definition: 'Flúor aplicado en pasta, gel, barniz o enjuague para favorecer remineralización.' },
      { term: 'Gingivitis', definition: 'Inflamación reversible de encías causada comúnmente por placa.' },
    ],
    faqs: [
      { question: '¿Cada cuánto necesito limpieza?', answer: 'Depende del riesgo de caries, sarro, encía, ortodoncia y hábitos. Muchas personas acuden cada 6 meses, pero algunos pacientes requieren controles más frecuentes.' },
      { question: '¿Si no duele puedo tener caries?', answer: 'Sí. La caries inicial o moderada puede no doler. Cuando aparece dolor espontáneo, puede estar más avanzada.' },
    ],
    relatedSlugs: ['brackets-metalicos-esteticos-y-cuidados', 'que-es-una-endodoncia', 'dolor-dental-cuando-es-urgencia'],
    sources: [
      { label: 'NOM-013-SSA2-2015, prevención y control de enfermedades bucales', url: 'https://www.cenaprece.salud.gob.mx/programas/interior/saludbucal/descargas/pdf/NORMA013.pdf' },
    ],
  },
  {
    slug: 'dolor-dental-cuando-es-urgencia',
    category: 'urgencias',
    eyebrow: 'Urgencias dentales',
    title: 'Dolor dental: cuándo esperar y cuándo acudir de urgencia',
    description: 'Señales de alarma, qué evitar en casa y por qué el dolor fuerte no debe taparse sólo con analgésicos.',
    readingTime: '7 min',
    heroSummary: 'El dolor dental puede venir de caries profunda, inflamación pulpar, infección, fractura, encía, trauma o mordida. Algunas molestias pueden esperar a una cita programada, pero otras requieren atención pronta para evitar complicaciones.',
    keyTakeaways: [
      'Inflamación en cara, fiebre, pus o dificultad para tragar/respirar son señales de urgencia.',
      'Antibióticos no sustituyen tratamiento dental; no deben tomarse sin indicación profesional.',
      'No coloques aspirina, alcohol, cloro, ajo u otros irritantes sobre encía o diente.',
      'Un diagnóstico correcto evita tratar el diente equivocado o retrasar una infección.',
    ],
    sections: [
      { heading: 'Señales de alarma', body: ['Acude lo antes posible si hay inflamación facial, fiebre, malestar general, pus, dolor que no cede, dificultad para abrir la boca, tragar o respirar, golpe con diente flojo, fractura grande o sangrado que no se controla.', 'Las infecciones odontogénicas pueden avanzar hacia tejidos de cara y cuello. Mientras antes se valore, mejor pronóstico suele tener el tratamiento.'] },
      { heading: 'Qué puedes hacer temporalmente', body: ['Mantén higiene suave, evita masticar con el lado afectado, usa compresas frías si hay trauma/inflamación reciente y sigue analgésicos sólo si son seguros para ti y no tienes contraindicaciones. Agenda valoración.', 'No apliques sustancias irritantes. Pueden quemar mucosa, empeorar dolor o dificultar el diagnóstico.'] },
      { heading: 'Por qué duele más en la noche', body: ['Al acostarte puede aumentar la presión sanguínea en la zona y percibirse más el dolor. También hay menos distracciones, por eso el dolor pulpar se vuelve muy notorio durante la noche.', 'Si el dolor espontáneo te despierta o dura mucho después de frío/calor, puede existir inflamación pulpar irreversible y requerir tratamiento específico.'] },
    ],
    glossary: [
      { term: 'Dolor espontáneo', definition: 'Dolor que aparece sin estímulo claro, incluso en reposo.' },
      { term: 'Celulitis facial', definition: 'Inflamación/infección extendida en tejidos blandos; requiere atención pronta.' },
      { term: 'Pulpitis', definition: 'Inflamación de la pulpa dental; puede ser reversible o irreversible.' },
    ],
    faqs: [
      { question: '¿Puedo tomar antibiótico?', answer: 'No lo tomes sin indicación. Muchas causas de dolor requieren tratamiento local, no sólo medicamento. El uso incorrecto favorece resistencia y puede ocultar síntomas.' },
      { question: '¿Una bolita en la encía es peligrosa?', answer: 'Puede ser una fístula por infección crónica. Aunque no duela, requiere valoración porque indica drenaje de infección.' },
    ],
    relatedSlugs: ['que-es-una-endodoncia', 'caries-placa-y-prevencion', 'diccionario-dental-basico'],
    sources: [
      { label: 'American Dental Association - Root canal treatment', url: 'https://jada.ada.org/article/S0002-8177(14)61651-2/fulltext' },
      { label: 'NOM-013-SSA2-2015, prevención y control de enfermedades bucales', url: 'https://www.cenaprece.salud.gob.mx/programas/interior/saludbucal/descargas/pdf/NORMA013.pdf' },
    ],
  },
  {
  "slug": "gingivitis-y-periodontitis-senales-tratamiento-prevencion",
  "category": "prevencion",
  "eyebrow": "Encías sanas",
  "title": "Gingivitis y periodontitis: señales, tratamiento y prevención",
  "description": "Cómo diferenciar encía inflamada de enfermedad periodontal, qué señales vigilar y por qué el sangrado no debe normalizarse.",
  "readingTime": "10 min",
  "heroSummary": "La encía no debería sangrar de forma habitual. El sangrado al cepillarte, la inflamación, el mal aliento persistente o la movilidad dental pueden indicar desde gingivitis reversible hasta periodontitis, una enfermedad que afecta el soporte del diente. Detectarla a tiempo evita tratamientos más complejos.",
  "keyTakeaways": [
    "La gingivitis suele relacionarse con placa bacteriana y puede mejorar con limpieza profesional, técnica de cepillado y control diario.",
    "La periodontitis afecta hueso y ligamento periodontal; puede causar retracción de encía, movilidad y pérdida dental.",
    "El sangrado no se resuelve dejando de cepillar; normalmente requiere mejor higiene y valoración dental.",
    "Diabetes, tabaquismo, embarazo, medicamentos, estrés y genética pueden aumentar el riesgo periodontal."
  ],
  "sections": [
    {
      "heading": "Por qué sangran las encías",
      "body": [
        "La causa más común es acumulación de placa bacteriana en el margen de la encía. Esa placa irrita el tejido, produce inflamación y facilita el sangrado durante cepillado, uso de hilo dental o al morder alimentos firmes.",
        "También puede haber factores que aumenten la respuesta inflamatoria: cambios hormonales, diabetes mal controlada, tabaquismo, respiración bucal, restauraciones desajustadas, ortodoncia con higiene deficiente o ciertos medicamentos. Por eso la valoración debe mirar más que sólo el síntoma."
      ]
    },
    {
      "heading": "Gingivitis no es lo mismo que periodontitis",
      "body": [
        "La gingivitis es inflamación de la encía sin pérdida irreversible del soporte del diente. Suele manifestarse con enrojecimiento, hinchazón, sensibilidad y sangrado. Con control de placa y limpieza profesional puede mejorar mucho.",
        "La periodontitis implica destrucción del periodonto: encía, ligamento periodontal, cemento radicular y hueso alveolar. Puede formar bolsas periodontales, retraer encía, generar movilidad y cambiar la forma en que muerden los dientes."
      ]
    },
    {
      "heading": "Cómo se diagnostica",
      "body": [
        "El dentista revisa encía, sarro, sangrado, movilidad y profundidad de bolsas periodontales con una sonda. En casos necesarios se solicitan radiografías para valorar nivel óseo y detectar lesiones que no se ven a simple vista.",
        "No basta con ver si hay sangre. Dos pacientes pueden sangrar igual, pero uno tener gingivitis leve y otro presentar enfermedad periodontal avanzada. Por eso medir y registrar es importante."
      ]
    },
    {
      "heading": "Tratamiento y mantenimiento",
      "body": [
        "En gingivitis, el tratamiento suele incluir profilaxis, instrucciones de higiene y revisión de factores retentivos como restauraciones desbordadas. En periodontitis puede requerirse raspado y alisado radicular, controles más frecuentes y seguimiento periodontal.",
        "El mantenimiento es clave. La enfermedad periodontal tiende a ser crónica: puede estabilizarse, pero requiere hábitos constantes, citas de control y evitar factores de riesgo como fumar."
      ]
    },
    {
      "heading": "Qué hacer en casa",
      "body": [
        "Cepilla encía y dientes con técnica suave pero completa; usa hilo dental o cepillos interdentales según tus espacios; cambia el cepillo cuando esté abierto; limpia la lengua y evita automedicarte con enjuagues agresivos sin indicación.",
        "Si sangras más al iniciar higiene, no suspendas automáticamente el cepillado. Agenda revisión para saber si necesitas limpieza profesional, ajuste de técnica o tratamiento periodontal."
      ]
    }
  ],
  "glossary": [
    {
      "term": "Gingivitis",
      "definition": "Inflamación de encía, generalmente reversible, comúnmente asociada a placa bacteriana."
    },
    {
      "term": "Periodontitis",
      "definition": "Enfermedad que afecta tejidos de soporte del diente y puede causar pérdida de hueso."
    },
    {
      "term": "Bolsa periodontal",
      "definition": "Espacio profundo entre diente y encía donde se acumulan bacterias y sarro subgingival."
    },
    {
      "term": "Raspado y alisado radicular",
      "definition": "Limpieza profunda de raíces para retirar sarro y toxinas bajo la encía."
    },
    {
      "term": "Sarro subgingival",
      "definition": "Cálculo dental acumulado debajo del margen de la encía."
    }
  ],
  "faqs": [
    {
      "question": "¿Es normal que me sangre la encía al usar hilo dental?",
      "answer": "No debería ser algo permanente. Puede sangrar al iniciar si hay inflamación, pero si continúa después de varios días de higiene adecuada conviene valoración."
    },
    {
      "question": "¿Una limpieza dental afloja los dientes?",
      "answer": "No. Si un diente se siente flojo después de retirar mucho sarro, normalmente el soporte ya estaba afectado; la limpieza revela el problema, no lo causa."
    },
    {
      "question": "¿La periodontitis se cura?",
      "answer": "Se puede controlar y estabilizar, pero requiere mantenimiento. El hueso perdido no siempre se recupera por completo."
    }
  ],
  "relatedSlugs": [
    "caries-placa-y-prevencion",
    "limpieza-dental-profesional-mitos-realidades",
    "diccionario-dental-basico"
  ],
  "sources": [
    {
      "label": "OMS - Salud bucodental",
      "url": "https://www.who.int/es/news-room/fact-sheets/detail/oral-health"
    },
    {
      "label": "Secretaría de Salud - caries, gingivitis y maloclusión en México",
      "url": "https://www.gob.mx/salud/prensa/082-caries-gingivitis-y-maloclusion-afecciones-bucales-mas-comunes-entre-la-poblacion-mexicana"
    },
    {
      "label": "NOM-013-SSA2-2015, prevención y control de enfermedades bucales",
      "url": "https://www.cenaprece.salud.gob.mx/programas/interior/saludbucal/descargas/pdf/NORMA013.pdf"
    }
  ]
},
  {
  "slug": "caries-pulpitis-endodoncia-diferencias",
  "category": "endodoncia",
  "eyebrow": "Dolor y conductos",
  "title": "Caries, pulpitis y endodoncia: diferencias que debes conocer",
  "description": "Entiende cómo avanza una caries, cuándo se inflama el nervio y por qué a veces una resina ya no es suficiente.",
  "readingTime": "11 min",
  "heroSummary": "Una caries no siempre empieza con dolor. Primero puede afectar esmalte y dentina; si avanza hacia la pulpa dental, puede provocar pulpitis reversible o irreversible, necrosis e infección. La diferencia define si basta una restauración o si se necesita endodoncia.",
  "keyTakeaways": [
    "La caries temprana puede no doler; esperar al dolor suele significar más daño.",
    "La pulpitis reversible puede mejorar si se retira la causa; la irreversible suele requerir tratamiento de conductos o extracción.",
    "Dolor espontáneo, nocturno o sensibilidad que tarda en desaparecer son señales importantes.",
    "Una radiografía ayuda, pero el diagnóstico también depende de pruebas clínicas de frío, percusión, mordida y exploración."
  ],
  "sections": [
    {
      "heading": "Cómo progresa una caries",
      "body": [
        "La caries es un proceso en el que bacterias producen ácidos que desmineralizan el diente. Al inicio puede verse como mancha blanca o pigmentación; después forma cavidad y avanza hacia dentina, donde el daño suele progresar más rápido.",
        "Cuando la caries se acerca a la pulpa, el diente puede reaccionar con sensibilidad al frío, dulce o masticación. En esta fase aún puede existir oportunidad de restaurar, pero depende de profundidad y respuesta del diente."
      ]
    },
    {
      "heading": "Qué es pulpitis reversible",
      "body": [
        "La pulpitis reversible es una inflamación pulpar leve o moderada que puede mejorar si se elimina el estímulo: caries, filtración, restauración alta o irritación. Suele doler con frío o dulce, pero el dolor se va rápido al retirar el estímulo.",
        "Esto no significa que siempre baste una resina. Si la cavidad es profunda, puede requerirse protección pulpar, restauración bien sellada y seguimiento para confirmar que el diente responde."
      ]
    },
    {
      "heading": "Qué es pulpitis irreversible",
      "body": [
        "La pulpitis irreversible aparece cuando la inflamación ya no puede regresar a la normalidad. Suele causar dolor espontáneo, dolor que despierta por la noche o sensibilidad al frío/calor que permanece varios segundos o minutos.",
        "En estos casos, colocar sólo una resina puede dejar el problema activo. El tratamiento más común para conservar el diente es la endodoncia, siempre que la pieza sea restaurable."
      ]
    },
    {
      "heading": "Necrosis, absceso y fístula",
      "body": [
        "Si la pulpa muere, el dolor puede disminuir temporalmente, pero la infección puede avanzar hacia la raíz y el hueso. Puede aparecer inflamación, dolor al morder, absceso o una fístula en encía.",
        "Que deje de doler no siempre es buena señal. Una infección crónica puede estar drenando sin mucho dolor, pero seguir afectando tejidos alrededor de la raíz."
      ]
    },
    {
      "heading": "Por qué la valoración temprana ahorra tratamientos",
      "body": [
        "Una caries pequeña suele requerir tratamientos más conservadores. Una caries profunda puede necesitar endodoncia, poste, reconstrucción o corona. Si el diente se fractura o pierde soporte, puede terminar en extracción.",
        "La prevención real no es sólo cepillarse: también implica revisiones, radiografías cuando están indicadas, dieta con menor frecuencia de azúcares y atención temprana de sensibilidad."
      ]
    }
  ],
  "glossary": [
    {
      "term": "Esmalte",
      "definition": "Capa externa dura del diente; no tiene nervios."
    },
    {
      "term": "Dentina",
      "definition": "Tejido debajo del esmalte; puede transmitir sensibilidad hacia la pulpa."
    },
    {
      "term": "Pulpitis reversible",
      "definition": "Inflamación pulpar que puede mejorar al eliminar la causa."
    },
    {
      "term": "Pulpitis irreversible",
      "definition": "Inflamación pulpar que generalmente requiere endodoncia o extracción."
    },
    {
      "term": "Necrosis pulpar",
      "definition": "Muerte de la pulpa dental, frecuentemente asociada a infección."
    }
  ],
  "faqs": [
    {
      "question": "¿Una caries profunda siempre necesita endodoncia?",
      "answer": "No siempre. Depende de síntomas, profundidad, exposición pulpar, pruebas de vitalidad y posibilidad de sellar correctamente el diente."
    },
    {
      "question": "¿Por qué duele si no se ve un hoyo grande?",
      "answer": "Puede haber caries entre dientes, fisuras, filtración bajo restauración o problemas de mordida. No todo se ve a simple vista."
    },
    {
      "question": "¿Puedo esperar si el dolor se quitó?",
      "answer": "No conviene asumir que se curó. A veces el dolor baja cuando la pulpa muere, pero la infección continúa."
    }
  ],
  "relatedSlugs": [
    "que-es-una-endodoncia",
    "dolor-dental-cuando-es-urgencia",
    "diccionario-dental-basico"
  ],
  "sources": [
    {
      "label": "American Association of Endodontists - Root Canal Treatment",
      "url": "https://www.aae.org/patients/root-canal-treatment/"
    },
    {
      "label": "Mayo Clinic - Root canal treatment",
      "url": "https://www.mayoclinic.org/diseases-conditions/tooth-abscess/in-depth/root-canal/art-20585454"
    },
    {
      "label": "NOM-013-SSA2-2015, prevención y control de enfermedades bucales",
      "url": "https://www.cenaprece.salud.gob.mx/programas/interior/saludbucal/descargas/pdf/NORMA013.pdf"
    }
  ]
},
  {
  "slug": "retenedores-dentales-despues-de-brackets",
  "category": "ortodoncia",
  "eyebrow": "Después de ortodoncia",
  "title": "Retenedores dentales: por qué son tan importantes después de brackets",
  "description": "Qué hacen los retenedores, tipos comunes, tiempo de uso y errores que pueden mover tus dientes otra vez.",
  "readingTime": "9 min",
  "heroSummary": "El tratamiento de ortodoncia no termina cuando se retiran los brackets. Los dientes tienen tendencia a moverse por memoria de tejidos, crecimiento, hábitos y fuerzas de mordida. La retención mantiene el resultado mientras los tejidos se estabilizan.",
  "keyTakeaways": [
    "Sin retenedores, los dientes pueden volver parcial o totalmente hacia su posición previa.",
    "Existen retenedores removibles y fijos; cada uno tiene ventajas, cuidados y riesgos.",
    "El tiempo de uso lo define el ortodoncista; muchas veces la retención nocturna se mantiene a largo plazo.",
    "Si el retenedor se rompe, se pierde o ya no entra, debe revisarse pronto para evitar movimientos."
  ],
  "sections": [
    {
      "heading": "Por qué los dientes se mueven después",
      "body": [
        "Durante la ortodoncia se remodela hueso y ligamento periodontal. Al retirar los aparatos, los tejidos necesitan tiempo para estabilizarse. Además, la mordida, lengua, labios, respiración, erupción de terceros molares y hábitos pueden influir en la posición dental.",
        "La recidiva no siempre significa que el tratamiento estuvo mal. Es una tendencia biológica conocida que se controla con retención y controles."
      ]
    },
    {
      "heading": "Tipos de retenedores",
      "body": [
        "Los retenedores removibles pueden ser transparentes tipo férula o de acrílico con alambre. Se quitan para comer y cepillar, pero dependen de disciplina del paciente. Si no se usan, no funcionan.",
        "Los retenedores fijos son alambres adheridos por la parte interna de los dientes, comúnmente anteriores. Son discretos y constantes, pero requieren higiene cuidadosa y revisión si se despegan."
      ]
    },
    {
      "heading": "Cuánto tiempo se usan",
      "body": [
        "La indicación varía según edad, severidad del apiñamiento, tipo de mordida, hábitos y estabilidad lograda. Es común indicar uso de tiempo completo al inicio y después uso nocturno, pero no debe generalizarse sin valoración.",
        "Muchos pacientes necesitan retención a largo plazo. Pensarlo como mantenimiento de sonrisa ayuda a evitar frustración: igual que una limpieza dental, la retención conserva resultados."
      ]
    },
    {
      "heading": "Errores frecuentes",
      "body": [
        "Guardar el retenedor en servilletas, lavarlo con agua caliente, dejarlo al alcance de mascotas, usarlo si está deformado o ignorar que ya aprieta demasiado son errores comunes.",
        "Si un retenedor transparente entra con presión intensa, puede significar que los dientes se movieron. Forzarlo sin indicación puede lastimar o no corregir el problema."
      ]
    },
    {
      "heading": "Higiene y revisiones",
      "body": [
        "Los retenedores removibles deben cepillarse suavemente y guardarse secos en su caja. Los fijos requieren cepillos interdentales o hilo especial para evitar sarro e inflamación.",
        "Agenda revisión si notas movilidad del alambre, resina despegada, mal olor persistente, fisuras o cambios en mordida."
      ]
    }
  ],
  "glossary": [
    {
      "term": "Retención",
      "definition": "Fase posterior a ortodoncia destinada a mantener los dientes en su nueva posición."
    },
    {
      "term": "Recidiva",
      "definition": "Movimiento de los dientes hacia posiciones previas después del tratamiento."
    },
    {
      "term": "Retenedor fijo",
      "definition": "Alambre adherido por detrás de los dientes para mantener alineación."
    },
    {
      "term": "Retenedor removible",
      "definition": "Aparato que el paciente coloca y retira según indicación."
    },
    {
      "term": "Férula transparente",
      "definition": "Retenedor plástico delgado hecho a la medida de los dientes."
    }
  ],
  "faqs": [
    {
      "question": "¿Puedo dejar de usar retenedor después de un año?",
      "answer": "No sin indicación. Algunos casos requieren retención nocturna por muchos años para mantener resultados."
    },
    {
      "question": "¿El retenedor fijo reemplaza al removible?",
      "answer": "Depende del caso. A veces se usan ambos porque protegen de formas distintas."
    },
    {
      "question": "¿Qué hago si perdí mi retenedor?",
      "answer": "Agenda pronto. Mientras más tiempo pase, mayor probabilidad de que los dientes se muevan y el retenedor nuevo ya no copie el resultado original."
    }
  ],
  "relatedSlugs": [
    "brackets-metalicos-esteticos-y-cuidados",
    "cuanto-dura-la-ortodoncia",
    "diccionario-dental-basico"
  ],
  "sources": [
    {
      "label": "American Association of Orthodontists - Life after braces",
      "url": "https://aaoinfo.org/whats-trending/life-after-braces/"
    },
    {
      "label": "Facultad de Odontología UNAM - Atención a pacientes ortodoncia",
      "url": "https://www.odonto.unam.mx/es/atencion-pacientes-ortodoncia"
    }
  ]
},
  {
  "slug": "bruxismo-apretar-dientes-guarda-oclusal",
  "category": "prevencion",
  "eyebrow": "Dolor mandibular y desgaste",
  "title": "Bruxismo: causas, síntomas y cuándo usar guarda oclusal",
  "description": "Aprende a identificar apretamiento dental, desgaste, dolor mandibular y el papel real de las guardas nocturnas.",
  "readingTime": "10 min",
  "heroSummary": "El bruxismo es apretar o rechinar los dientes, despierto o dormido. Puede desgastar esmalte, fracturar restauraciones, provocar dolor muscular, sensibilidad dental y molestias en la articulación temporomandibular. El tratamiento depende de causa, severidad y daño observado.",
  "keyTakeaways": [
    "No todas las personas que rechinan necesitan el mismo tratamiento; primero hay que valorar desgaste, dolor y hábitos.",
    "La guarda oclusal protege dientes y restauraciones, pero no siempre elimina la causa del bruxismo.",
    "Estrés, sueño, medicamentos, alcohol, cafeína y trastornos respiratorios pueden influir.",
    "Dolor mandibular, dolor de cabeza al despertar, dientes planos o fracturas repetidas son señales para revisión."
  ],
  "sections": [
    {
      "heading": "Cómo se manifiesta el bruxismo",
      "body": [
        "Algunas personas rechinan con ruido durante la noche; otras aprietan sin hacer sonido. Los signos pueden ser desgaste dental, bordes fracturados, sensibilidad, dolor al masticar, músculos tensos, chasquidos articulares o dolor de cabeza al despertar.",
        "También puede detectarse porque se rompen resinas, coronas o férulas con frecuencia. No siempre el paciente se da cuenta; a veces lo reporta la pareja o el dentista durante revisión."
      ]
    },
    {
      "heading": "Causas y factores asociados",
      "body": [
        "El bruxismo suele ser multifactorial. Puede relacionarse con estrés, ansiedad, concentración intensa, calidad del sueño, consumo de cafeína o alcohol, tabaquismo, algunos medicamentos y condiciones médicas.",
        "En niños puede aparecer y desaparecer con el crecimiento. En adultos, si hay dolor, desgaste o fracturas, conviene valorar antes de que avance el daño."
      ]
    },
    {
      "heading": "Qué hace una guarda oclusal",
      "body": [
        "Una guarda oclusal bien adaptada crea una barrera protectora entre dientes, distribuye fuerzas y ayuda a reducir daño en esmalte o restauraciones. Debe fabricarse a medida y ajustarse clínicamente.",
        "No es lo mismo una guarda profesional que una placa genérica comprada sin ajuste. Una guarda mal adaptada puede generar molestias, cambios de mordida o no proteger adecuadamente."
      ]
    },
    {
      "heading": "Tratamiento integral",
      "body": [
        "Además de guarda, pueden requerirse ajustes de hábitos, manejo de estrés, terapia física, revisión de mordida, tratamiento de dolor muscular o derivación si se sospechan problemas de sueño.",
        "El objetivo no siempre es eliminar al 100% el bruxismo, sino controlar daño, dolor y factores que lo agravan."
      ]
    },
    {
      "heading": "Cuándo acudir",
      "body": [
        "Agenda revisión si tienes dolor en mandíbula, dientes sensibles sin caries visible, fracturas repetidas, desgaste notable, chasquidos con dolor, limitación para abrir la boca o dolor de cabeza frecuente al despertar.",
        "Si ya usas guarda, llévala a tus citas para revisar desgaste, ajuste y limpieza."
      ]
    }
  ],
  "glossary": [
    {
      "term": "Bruxismo",
      "definition": "Apretar o rechinar dientes de forma involuntaria o repetitiva."
    },
    {
      "term": "Guarda oclusal",
      "definition": "Dispositivo hecho a medida para proteger dientes de fuerzas excesivas."
    },
    {
      "term": "ATM",
      "definition": "Articulación temporomandibular, une mandíbula con cráneo."
    },
    {
      "term": "Faceta de desgaste",
      "definition": "Área plana o brillante en dientes causada por fricción."
    },
    {
      "term": "Mialgia",
      "definition": "Dolor muscular; en odontología puede afectar músculos masticadores."
    }
  ],
  "faqs": [
    {
      "question": "¿La guarda cura el bruxismo?",
      "answer": "Generalmente protege dientes y reduce consecuencias, pero no siempre elimina la causa. Puede necesitarse manejo adicional."
    },
    {
      "question": "¿Puedo comprar una guarda genérica?",
      "answer": "No es lo ideal. Las guardas requieren ajuste a tu mordida; una mal adaptada puede generar molestias o no proteger."
    },
    {
      "question": "¿El bruxismo puede romper dientes?",
      "answer": "Sí, en casos severos puede contribuir a fracturas, desgaste y falla de restauraciones."
    }
  ],
  "relatedSlugs": [
    "dolor-dental-cuando-es-urgencia",
    "limpieza-dental-profesional-mitos-realidades",
    "diccionario-dental-basico"
  ],
  "sources": [
    {
      "label": "Mayo Clinic - Bruxism diagnosis and treatment",
      "url": "https://www.mayoclinic.org/diseases-conditions/bruxism/diagnosis-treatment/drc-20356100"
    },
    {
      "label": "Mayo Clinic - Bruxism symptoms and causes",
      "url": "https://www.mayoclinic.org/diseases-conditions/bruxism/symptoms-causes/syc-20356095"
    }
  ]
},
  {
  "slug": "muelas-del-juicio-cuando-extraerlas",
  "category": "urgencias",
  "eyebrow": "Cirugía oral",
  "title": "Muelas del juicio: cuándo extraerlas y señales de alarma",
  "description": "Qué son los terceros molares, por qué se impactan, cuándo se recomienda retirarlos y cómo cuidarte después.",
  "readingTime": "10 min",
  "heroSummary": "Las muelas del juicio o terceros molares aparecen al final de la adolescencia o en adultez temprana. Algunas erupcionan sin problema, pero otras quedan retenidas, parcialmente cubiertas por encía o mal posicionadas, causando dolor, infección, caries o daño al segundo molar.",
  "keyTakeaways": [
    "No todas las muelas del juicio se extraen; se decide por síntomas, posición, espacio, higiene y riesgo futuro.",
    "Dolor con inflamación, mal sabor, dificultad para abrir la boca o fiebre requiere valoración pronta.",
    "La radiografía panorámica ayuda a valorar raíces, nervio dentario y posición del tercer molar.",
    "Después de extracción, seguir indicaciones reduce riesgo de alveolitis, sangrado o infección."
  ],
  "sections": [
    {
      "heading": "Qué significa que esté impactada o retenida",
      "body": [
        "Una muela del juicio impactada no logra salir correctamente por falta de espacio, posición inclinada o bloqueo por hueso/diente vecino. Puede quedar dentro del hueso o parcialmente cubierta por encía.",
        "Cuando sale sólo una parte, se forma una zona difícil de limpiar donde se acumulan bacterias y restos de comida. Esto puede causar pericoronitis, inflamación y dolor."
      ]
    },
    {
      "heading": "Cuándo se recomienda extracción",
      "body": [
        "Puede indicarse extracción si hay infecciones repetidas, caries en el tercer molar o en el segundo molar, dolor, quistes, daño periodontal, falta de espacio o imposibilidad de higiene adecuada.",
        "En otros casos se decide observar con controles. La decisión debe considerar edad, anatomía, enfermedades, medicamentos, embarazo, riesgo quirúrgico y cercanía al nervio."
      ]
    },
    {
      "heading": "Señales de alarma",
      "body": [
        "Busca atención si hay inflamación de cara, fiebre, pus, dolor intenso, dificultad para abrir la boca, tragar o respirar, sangrado persistente o mal olor severo.",
        "No intentes drenar o manipular la encía en casa. La automedicación puede ocultar síntomas y retrasar el tratamiento adecuado."
      ]
    },
    {
      "heading": "Qué esperar del procedimiento",
      "body": [
        "Antes de extraer se revisa historia clínica, exploración y radiografía. Algunas extracciones son simples; otras requieren cirugía, levantar encía, retirar hueso o dividir la pieza.",
        "La dificultad depende de posición, forma de raíces, densidad ósea, edad y relación con estructuras anatómicas."
      ]
    },
    {
      "heading": "Cuidados después",
      "body": [
        "Las indicaciones pueden incluir morder gasa, reposo relativo, dieta blanda, evitar popote, no fumar, no escupir fuerte y mantener higiene cuidadosa. El objetivo es conservar el coágulo y permitir cicatrización.",
        "Dolor que aumenta al tercer o cuarto día con mal olor puede sugerir alveolitis; requiere revisión."
      ]
    }
  ],
  "glossary": [
    {
      "term": "Tercer molar",
      "definition": "Muela del juicio; último molar en aparecer."
    },
    {
      "term": "Impactación",
      "definition": "Cuando un diente no puede erupcionar correctamente por bloqueo o mala posición."
    },
    {
      "term": "Pericoronitis",
      "definition": "Inflamación o infección de la encía que cubre parcialmente una muela."
    },
    {
      "term": "Alveolitis",
      "definition": "Dolor postextracción asociado a pérdida o alteración del coágulo."
    },
    {
      "term": "Radiografía panorámica",
      "definition": "Imagen amplia que muestra dientes, maxilares y estructuras vecinas."
    }
  ],
  "faqs": [
    {
      "question": "¿Siempre hay que quitar las muelas del juicio?",
      "answer": "No. Si están sanas, bien posicionadas, funcionales y se pueden limpiar, pueden vigilarse. La extracción depende del diagnóstico."
    },
    {
      "question": "¿Duele la extracción?",
      "answer": "Con anestesia no debería doler durante el procedimiento, aunque puede sentirse presión. Después puede haber inflamación y molestia controlable con indicaciones."
    },
    {
      "question": "¿Cuántos días dura la recuperación?",
      "answer": "Varía. Muchas molestias bajan en pocos días, pero la cicatrización completa tarda más. Sigue indicaciones específicas del cirujano o dentista."
    }
  ],
  "relatedSlugs": [
    "dolor-dental-cuando-es-urgencia",
    "gingivitis-y-periodontitis-senales-tratamiento-prevencion",
    "diccionario-dental-basico"
  ],
  "sources": [
    {
      "label": "Facultad de Odontología UNAM - Cirugía Oral y Maxilofacial",
      "url": "https://www.odonto.unam.mx/es/ingeso-de-pacientes"
    },
    {
      "label": "Mayo Clinic - Wisdom tooth extraction",
      "url": "https://www.mayoclinic.org/tests-procedures/wisdom-tooth-extraction/about/pac-20395268"
    }
  ]
},
  {
  "slug": "limpieza-dental-profesional-mitos-realidades",
  "category": "prevencion",
  "eyebrow": "Prevención práctica",
  "title": "Limpieza dental profesional: mitos, beneficios y cada cuánto hacerla",
  "description": "Qué retira una limpieza, por qué no desgasta dientes sanos y cuándo se necesita algo más profundo que profilaxis.",
  "readingTime": "9 min",
  "heroSummary": "La limpieza dental profesional ayuda a retirar placa, sarro y pigmentaciones que el cepillo no puede eliminar. Es una de las formas más simples de prevenir gingivitis, caries y mal aliento, pero debe diferenciarse de tratamientos periodontales más profundos.",
  "keyTakeaways": [
    "La limpieza profesional no desgasta el esmalte cuando se realiza correctamente.",
    "El sarro no se elimina con cepillado; requiere instrumental dental.",
    "La frecuencia depende de riesgo de caries, encía, ortodoncia, tabaquismo, diabetes y acumulación de sarro.",
    "Si hay bolsas periodontales, puede requerirse raspado y alisado radicular, no sólo profilaxis."
  ],
  "sections": [
    {
      "heading": "Qué es una profilaxis",
      "body": [
        "La profilaxis es una limpieza profesional enfocada en retirar placa, sarro supragingival y manchas externas. Se complementa con pulido, instrucciones de higiene y evaluación de encías y dientes.",
        "No sustituye el cepillado diario. Funciona mejor cuando el paciente mantiene hábitos adecuados en casa."
      ]
    },
    {
      "heading": "Por qué se forma sarro",
      "body": [
        "La placa bacteriana es una película blanda que se forma todos los días. Si no se remueve bien, minerales de la saliva la endurecen y se convierte en sarro o cálculo dental.",
        "El sarro retiene más placa y dificulta la higiene, por eso se relaciona con inflamación de encía, mal aliento y enfermedad periodontal."
      ]
    },
    {
      "heading": "Mitos comunes",
      "body": [
        "Mito: 'la limpieza afloja los dientes'. Realidad: retirar sarro no daña el soporte; si había movilidad, probablemente ya existía pérdida de soporte o inflamación.",
        "Mito: 'si no me duele, no necesito limpieza'. Realidad: caries, gingivitis y periodontitis pueden avanzar sin dolor al inicio."
      ]
    },
    {
      "heading": "Cada cuánto hacerla",
      "body": [
        "La recomendación general suele ser cada seis meses, pero no aplica igual para todos. Pacientes con ortodoncia, sarro frecuente, enfermedad periodontal, tabaquismo, diabetes o alto riesgo de caries pueden necesitar controles más cercanos.",
        "El intervalo debe definirse con el dentista según hallazgos clínicos, no sólo por calendario."
      ]
    },
    {
      "heading": "Cuándo no basta una limpieza sencilla",
      "body": [
        "Si hay bolsas periodontales, sarro bajo la encía, sangrado abundante, movilidad o pérdida ósea, puede requerirse raspado y alisado radicular, controles periodontales y seguimiento específico.",
        "La profilaxis es preventiva; la terapia periodontal trata enfermedad ya establecida."
      ]
    }
  ],
  "glossary": [
    {
      "term": "Profilaxis",
      "definition": "Limpieza dental profesional preventiva."
    },
    {
      "term": "Placa bacteriana",
      "definition": "Película blanda de bacterias que se forma sobre dientes y encías."
    },
    {
      "term": "Cálculo dental",
      "definition": "Sarro endurecido por minerales de la saliva."
    },
    {
      "term": "Pulido dental",
      "definition": "Procedimiento para suavizar superficies y retirar manchas externas."
    },
    {
      "term": "Mantenimiento periodontal",
      "definition": "Controles periódicos para pacientes con antecedente de enfermedad periodontal."
    }
  ],
  "faqs": [
    {
      "question": "¿La limpieza blanquea los dientes?",
      "answer": "Retira manchas externas y puede hacer que se vean más limpios, pero no cambia el color interno como un blanqueamiento."
    },
    {
      "question": "¿Duele una limpieza?",
      "answer": "Puede haber sensibilidad si hay inflamación o sarro abundante, pero no debería ser dolor intenso. Se puede adaptar la técnica según sensibilidad."
    },
    {
      "question": "¿Puedo hacerme limpieza con brackets?",
      "answer": "Sí, y suele ser muy recomendable porque los brackets retienen placa. Requiere técnica cuidadosa."
    }
  ],
  "relatedSlugs": [
    "gingivitis-y-periodontitis-senales-tratamiento-prevencion",
    "caries-placa-y-prevencion",
    "diccionario-dental-basico"
  ],
  "sources": [
    {
      "label": "Secretaría de Salud - caries, gingivitis y maloclusión en México",
      "url": "https://www.gob.mx/salud/prensa/082-caries-gingivitis-y-maloclusion-afecciones-bucales-mas-comunes-entre-la-poblacion-mexicana"
    },
    {
      "label": "NOM-013-SSA2-2015, prevención y control de enfermedades bucales",
      "url": "https://www.cenaprece.salud.gob.mx/programas/interior/saludbucal/descargas/pdf/NORMA013.pdf"
    },
    {
      "label": "OMS - Salud bucodental",
      "url": "https://www.who.int/es/news-room/fact-sheets/detail/oral-health"
    }
  ]
},
  {
  "slug": "diccionario-dental-basico",
  "category": "prevencion",
  "eyebrow": "Terminología dental",
  "title": "Diccionario dental básico para pacientes: 70 términos explicados fácil",
  "description": "Glosario dental amplio con términos de endodoncia, ortodoncia, encías, prevención, cirugía oral, prótesis y urgencias explicado en lenguaje claro.",
  "readingTime": "16 min",
  "heroSummary": "Este diccionario reúne términos que los pacientes escuchan en consulta dental en México: caries, resina, corona, endodoncia, conductos, brackets, retenedores, gingivitis, periodontitis, muelas del juicio y urgencias. La meta es que entiendas mejor tu diagnóstico y puedas preguntar con más claridad.",
  "keyTakeaways": [
    "El glosario ayuda a entender palabras clínicas, pero no sustituye exploración, radiografías ni diagnóstico profesional.",
    "Endodoncia se relaciona con pulpa, conductos, infección y restauración final del diente.",
    "Ortodoncia no sólo alinea dientes; también estudia oclusión, mordida, espacio y estabilidad.",
    "Encía que sangra, dolor persistente, inflamación o movilidad dental requieren valoración, no sólo buscar definiciones en internet."
  ],
  "sections": [
    {
      "heading": "Cómo usar este diccionario",
      "body": [
        "Usa esta guía como mapa rápido antes o después de tu cita. Si tu dentista menciona un término, búscalo aquí para entender qué significa, qué parte de la boca involucra y por qué puede importar en tu tratamiento.",
        "Muchas palabras se conectan entre sí: caries profunda puede llevar a pulpitis; pulpitis irreversible puede requerir endodoncia; un diente con endodoncia puede necesitar corona; y una mordida fuerte o bruxismo puede afectar la duración de esas restauraciones."
      ]
    },
    {
      "heading": "Endodoncia: nervio, conductos e infección",
      "body": [
        "La endodoncia se enfoca en problemas internos del diente: pulpa inflamada, conductos radiculares, necrosis, abscesos y lesiones alrededor de la raíz. El objetivo suele ser conservar el diente cuando todavía es restaurable.",
        "Palabras importantes: pulpa dental, cámara pulpar, conducto radicular, ápice, pulpitis reversible, pulpitis irreversible, necrosis pulpar, absceso, fístula, lesión periapical, obturación de conductos y retratamiento."
      ]
    },
    {
      "heading": "Ortodoncia: alineación, mordida y retención",
      "body": [
        "La ortodoncia corrige posición dental y relación de mordida. Los brackets, arcos, ligaduras, elásticos y retenedores trabajan junto con el hueso y ligamento periodontal para mover dientes de forma controlada.",
        "Palabras importantes: maloclusión, apiñamiento, diastema, mordida cruzada, mordida abierta, mordida profunda, overjet, overbite, arco ortodóncico, bracket, ligadura, elástico intermaxilar, retenedor fijo, retenedor removible y recidiva."
      ]
    },
    {
      "heading": "Encías y prevención",
      "body": [
        "La salud de encía es parte central de la odontología. Una encía inflamada puede sangrar por placa; si el problema avanza al soporte del diente, se habla de periodontitis. La prevención incluye higiene, profilaxis, control de sarro, flúor y revisiones.",
        "Palabras importantes: gingivitis, periodontitis, placa bacteriana, cálculo dental, bolsa periodontal, raspado y alisado radicular, profilaxis, fluoruro, sellador dental y mantenimiento periodontal."
      ]
    },
    {
      "heading": "Restauraciones, prótesis y cirugía oral",
      "body": [
        "Cuando un diente pierde estructura por caries, fractura o desgaste, puede repararse con resina, incrustación, corona, puente o prótesis, según el caso. En cirugía oral aparecen términos como exodoncia, alveolo, sutura, tercer molar, impactación y alveolitis.",
        "La opción adecuada depende de estructura remanente, soporte periodontal, mordida, estética, presupuesto y pronóstico. Por eso dos pacientes con 'una muela rota' pueden necesitar tratamientos distintos."
      ]
    }
  ],
  "glossary": [
    {
      "term": "Absceso dental",
      "definition": "Acumulación de pus por infección. Puede causar dolor, inflamación, fiebre o drenaje por encía."
    },
    {
      "term": "Alveolo",
      "definition": "Cavidad del hueso donde se alojaba la raíz de un diente extraído."
    },
    {
      "term": "Alveolitis",
      "definition": "Dolor postextracción relacionado con pérdida o desintegración del coágulo dentro del alveolo."
    },
    {
      "term": "Ápice radicular",
      "definition": "Punta de la raíz del diente; zona importante en infecciones endodónticas."
    },
    {
      "term": "Apiñamiento",
      "definition": "Falta de espacio que provoca dientes encimados o girados."
    },
    {
      "term": "Arco ortodóncico",
      "definition": "Alambre que conecta brackets y guía movimientos dentales."
    },
    {
      "term": "ATM",
      "definition": "Articulación temporomandibular; une mandíbula con cráneo y participa al abrir/cerrar la boca."
    },
    {
      "term": "Avulsión dental",
      "definition": "Salida completa de un diente por golpe. En dientes permanentes es una urgencia."
    },
    {
      "term": "Biopelícula",
      "definition": "Comunidad de bacterias adheridas a superficies dentales; también se conoce como placa bacteriana."
    },
    {
      "term": "Bolsa periodontal",
      "definition": "Espacio profundo entre encía y diente asociado a enfermedad periodontal."
    },
    {
      "term": "Bracket",
      "definition": "Pequeña pieza adherida al diente que sostiene el arco de ortodoncia."
    },
    {
      "term": "Bruxismo",
      "definition": "Apretar o rechinar dientes, despierto o dormido."
    },
    {
      "term": "Cálculo dental / sarro",
      "definition": "Placa bacteriana endurecida por minerales de la saliva; requiere limpieza profesional."
    },
    {
      "term": "Cámara pulpar",
      "definition": "Espacio interno de la corona del diente donde se encuentra la pulpa."
    },
    {
      "term": "Caries",
      "definition": "Proceso de destrucción dental causado por ácidos bacterianos, dieta, tiempo y susceptibilidad del diente."
    },
    {
      "term": "Cemento radicular",
      "definition": "Capa que cubre la raíz y ayuda a unir el diente al ligamento periodontal."
    },
    {
      "term": "Conducto radicular",
      "definition": "Canal dentro de la raíz donde pasa la pulpa dental."
    },
    {
      "term": "Corona dental natural",
      "definition": "Parte visible del diente por encima de la encía."
    },
    {
      "term": "Corona protésica",
      "definition": "Restauración que cubre y protege un diente debilitado o reemplaza corona sobre implante."
    },
    {
      "term": "Dentina",
      "definition": "Tejido bajo el esmalte; transmite estímulos y puede causar sensibilidad."
    },
    {
      "term": "Diastema",
      "definition": "Espacio entre dos dientes, comúnmente entre incisivos superiores."
    },
    {
      "term": "Elástico intermaxilar",
      "definition": "Liga usada en ortodoncia para ayudar a corregir relación entre arcadas."
    },
    {
      "term": "Esmalte",
      "definition": "Capa externa dura del diente. No se regenera como piel o hueso."
    },
    {
      "term": "Exodoncia",
      "definition": "Extracción dental."
    },
    {
      "term": "Faceta de desgaste",
      "definition": "Zona plana o brillante causada por fricción dental, frecuente en bruxismo."
    },
    {
      "term": "Férula dental",
      "definition": "Aparato removible a medida; puede usarse para retención, protección o diagnóstico."
    },
    {
      "term": "Fístula",
      "definition": "Pequeño conducto por donde puede drenar pus de una infección crónica."
    },
    {
      "term": "Fluoruro",
      "definition": "Mineral que ayuda a fortalecer esmalte y prevenir caries cuando se usa correctamente."
    },
    {
      "term": "Gingivitis",
      "definition": "Inflamación de encía, usualmente reversible, con sangrado y enrojecimiento."
    },
    {
      "term": "Guarda oclusal",
      "definition": "Férula rígida o semirrígida que protege dientes de fuerzas de bruxismo."
    },
    {
      "term": "Implante dental",
      "definition": "Tornillo de titanio o material biocompatible colocado en hueso para sostener una prótesis."
    },
    {
      "term": "Incrustación",
      "definition": "Restauración indirecta fabricada fuera de boca para reparar pérdida moderada o amplia de estructura."
    },
    {
      "term": "Instrumentación rotatoria",
      "definition": "Uso de limas mecanizadas para limpiar y conformar conductos en endodoncia."
    },
    {
      "term": "Lesión periapical",
      "definition": "Cambio en hueso alrededor del ápice, frecuentemente asociado a infección o inflamación pulpar."
    },
    {
      "term": "Ligadura",
      "definition": "Liga o alambre pequeño que sujeta el arco al bracket."
    },
    {
      "term": "Ligamento periodontal",
      "definition": "Tejido que une raíz dental al hueso y permite ligera movilidad fisiológica."
    },
    {
      "term": "Maloclusión",
      "definition": "Relación incorrecta entre dientes superiores e inferiores al morder."
    },
    {
      "term": "Mantenedor de espacio",
      "definition": "Aparato usado, sobre todo en niños, para conservar espacio tras pérdida temprana de un diente temporal."
    },
    {
      "term": "Mordida abierta",
      "definition": "Falta de contacto entre dientes superiores e inferiores en una zona al cerrar."
    },
    {
      "term": "Mordida cruzada",
      "definition": "Cuando dientes superiores muerden por dentro de los inferiores en una o varias zonas."
    },
    {
      "term": "Mordida profunda",
      "definition": "Cuando los dientes superiores cubren demasiado a los inferiores."
    },
    {
      "term": "Necrosis pulpar",
      "definition": "Muerte de la pulpa dental; puede asociarse a infección sin dolor inicial."
    },
    {
      "term": "Obturación de conductos",
      "definition": "Sellado interno del sistema de conductos después de limpiar y desinfectar."
    },
    {
      "term": "Oclusión",
      "definition": "Forma en que contactan dientes superiores e inferiores al cerrar la boca."
    },
    {
      "term": "Overbite",
      "definition": "Medida vertical de cuánto cubren los incisivos superiores a los inferiores."
    },
    {
      "term": "Overjet",
      "definition": "Distancia horizontal entre incisivos superiores e inferiores."
    },
    {
      "term": "Periodontitis",
      "definition": "Enfermedad que destruye soporte del diente: encía, ligamento y hueso."
    },
    {
      "term": "Periodonto",
      "definition": "Conjunto de tejidos que sostienen el diente: encía, hueso, cemento y ligamento periodontal."
    },
    {
      "term": "Pericoronitis",
      "definition": "Inflamación/infección alrededor de un diente parcialmente erupcionado, común en terceros molares."
    },
    {
      "term": "Placa bacteriana",
      "definition": "Película blanda de bacterias que se forma todos los días sobre dientes y encías."
    },
    {
      "term": "Profilaxis",
      "definition": "Limpieza dental profesional preventiva."
    },
    {
      "term": "Prótesis parcial",
      "definition": "Aparato que reemplaza algunos dientes ausentes."
    },
    {
      "term": "Puente dental",
      "definition": "Prótesis fija que reemplaza uno o más dientes apoyándose en dientes o implantes vecinos."
    },
    {
      "term": "Pulpa dental",
      "definition": "Tejido interno con nervios, vasos sanguíneos y tejido conectivo."
    },
    {
      "term": "Pulpitis irreversible",
      "definition": "Inflamación pulpar que no se recupera y suele requerir endodoncia o extracción."
    },
    {
      "term": "Pulpitis reversible",
      "definition": "Inflamación pulpar que puede mejorar si se elimina la causa."
    },
    {
      "term": "Radiografía panorámica",
      "definition": "Imagen amplia de dientes, maxilares y estructuras cercanas."
    },
    {
      "term": "Raspado y alisado radicular",
      "definition": "Tratamiento periodontal para retirar sarro y toxinas de raíz bajo la encía."
    },
    {
      "term": "Recidiva ortodóncica",
      "definition": "Movimiento de dientes después de ortodoncia hacia posiciones no deseadas."
    },
    {
      "term": "Resina dental",
      "definition": "Material restaurador del color del diente usado para caries o fracturas."
    },
    {
      "term": "Retenedor fijo",
      "definition": "Alambre adherido por detrás de dientes para conservar alineación."
    },
    {
      "term": "Retenedor removible",
      "definition": "Aparato que se quita y coloca para mantener resultados de ortodoncia."
    },
    {
      "term": "Retratamiento endodóntico",
      "definition": "Repetir una endodoncia previa por filtración, infección persistente o falla del tratamiento."
    },
    {
      "term": "Sellador dental",
      "definition": "Material preventivo colocado en surcos de molares para reducir riesgo de caries."
    },
    {
      "term": "Sensibilidad dentinaria",
      "definition": "Dolor breve ante frío, dulce, aire o cepillado por exposición de dentina."
    },
    {
      "term": "Sutura",
      "definition": "Punto usado para cerrar o estabilizar encía después de cirugía."
    },
    {
      "term": "Tercer molar",
      "definition": "Muela del juicio; último molar en aparecer."
    },
    {
      "term": "Trauma dental",
      "definition": "Lesión por golpe que puede fracturar, aflojar o desplazar dientes."
    }
  ],
  "faqs": [
    {
      "question": "¿Por qué mi dentista usa tantos términos técnicos?",
      "answer": "Porque ayudan a describir ubicación, causa y tratamiento. Pedir una explicación en lenguaje simple es totalmente válido."
    },
    {
      "question": "¿Qué término debo buscar si me duele con frío?",
      "answer": "Sensibilidad, dentina, caries, pulpitis reversible o irreversible. La duración del dolor después del frío es muy importante para el diagnóstico."
    },
    {
      "question": "¿Qué términos son señales de atención rápida?",
      "answer": "Absceso, celulitis facial, fístula, avulsión, trauma dental, inflamación facial, pulpitis irreversible o pericoronitis con fiebre."
    },
    {
      "question": "¿Este glosario aplica para México?",
      "answer": "Sí, usa terminología común en odontología clínica en México y se apoya en referencias de salud bucal, normas mexicanas y fuentes odontológicas internacionales."
    }
  ],
  "relatedSlugs": [
    "que-es-una-endodoncia",
    "brackets-metalicos-esteticos-y-cuidados",
    "gingivitis-y-periodontitis-senales-tratamiento-prevencion"
  ],
  "sources": [
    {
      "label": "NOM-013-SSA2-2015, prevención y control de enfermedades bucales",
      "url": "https://www.cenaprece.salud.gob.mx/programas/interior/saludbucal/descargas/pdf/NORMA013.pdf"
    },
    {
      "label": "OMS - Salud bucodental",
      "url": "https://www.who.int/es/news-room/fact-sheets/detail/oral-health"
    },
    {
      "label": "American Association of Orthodontists - Glossary",
      "url": "https://aaoinfo.org/resources/glossary-of-orthodontic-terms/"
    },
    {
      "label": "American Association of Endodontists - Root Canal Treatment",
      "url": "https://www.aae.org/patients/root-canal-treatment/"
    }
  ]
},
];

export const featuredEducationSlugs = ['que-es-una-endodoncia', 'brackets-metalicos-esteticos-y-cuidados', 'gingivitis-y-periodontitis-senales-tratamiento-prevencion'];

export const getEducationArticleBySlug = (slug?: string): DentalEducationArticle | undefined => {
  if (!slug) return undefined;
  return dentalEducationArticles.find((article) => article.slug === slug);
};

export const getEducationArticlesByCategory = (category: EducationCategory): DentalEducationArticle[] => {
  return dentalEducationArticles.filter((article) => article.category === category);
};
